import { useController, Control, FieldValues, FieldPath, PathValue } from "react-hook-form";

/**
 * A custom hook to bind react-hook-form state to @ucl/dse components.
 * It provides the necessary props for value, state changes, and error display.
 * @param props - The props for useController, typically just { name: "fieldName" }.
 */
export const useBoundInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: {
    name: TName;
    defaultValue?: PathValue<TFieldValues, TName>;
    control?: Control<TFieldValues>;
}) => {
    const {
        field,
        fieldState: { error },
    } = useController({
        name: props.name,
        defaultValue: props.defaultValue ?? ("" as PathValue<TFieldValues, TName>),
        control: props.control,
    });
    const inputState: "error" | "none" = error ? "error" : "none";

    const { ref, ...fieldWithoutRef } = field;

    return {
        ...fieldWithoutRef,
        ref,
        value: field.value ?? "",
        inputState,
        helperTextProp: error?.message ? { secondaryProp: { helperText: error.message } } : undefined,
    };
};
